import { AI_ASSET_STATUS } from '@/lib/ai-asset-status';
import type {
  GenerationReferenceHumanAsset,
  GenerationReferenceItem,
  GenerationTargetKey,
} from '@/types/generation-reference';

const EMPTY_TARGET_QUALIFIER = '_';

export function buildGenerationTargetCacheKey(target: GenerationTargetKey): string {
  if (target.tagId && target.planId) {
    throw new Error('tagId 和 planId 不能同时存在');
  }

  return [
    target.projectId,
    target.targetType,
    target.targetId,
    target.tagId ?? EMPTY_TARGET_QUALIFIER,
    target.planId ?? EMPTY_TARGET_QUALIFIER,
    target.mediaType,
  ].join(':');
}

/** 画布 history 查询缓存键：含 resourceId，同 plan 多视频互不覆盖。 */
export function buildCanvasHistoryQueryCacheKey(
  target: GenerationTargetKey,
  resourceId?: string | null
): string {
  return `${buildGenerationTargetCacheKey(target)}::${resourceId ?? ''}`;
}

export function buildGenerationReferenceIdentityKey(
  reference: Pick<GenerationReferenceItem, 'resourceType' | 'resourceId' | 'role'>
): string {
  return `${reference.resourceType}:${reference.resourceId}:${reference.role}`;
}

/** `resourceType:resourceId`：同一资源在不同 role 下共用的资源级键。 */
export function buildGenerationResourceKey(
  resource: Pick<GenerationReferenceItem, 'resourceType' | 'resourceId'>
): string {
  return `${resource.resourceType}:${resource.resourceId}`;
}

/**
 * 已通过人像认证的引用：identity → 人像资产类型（1=真人形象，2=虚拟人像）。
 *
 * 认证状态由服务端按「资源」实时解析，而素材条按「引用」渲染；
 * 同一资源可能有多条不同 role 的连线，这里展开到每条引用。
 * 保留资产类型而不是压成 Set：文案要区分真人形象与虚拟人像。
 */
export function buildCertifiedReferenceAssetTypes(
  references: readonly GenerationReferenceItem[],
  humanAssets: readonly GenerationReferenceHumanAsset[] | undefined
): ReadonlyMap<string, number | null | undefined> {
  const certifiedByResourceKey = new Map<string, number | null | undefined>();
  for (const humanAsset of humanAssets ?? []) {
    if (humanAsset.humanAssetStatus !== AI_ASSET_STATUS.ACTIVE) continue;
    certifiedByResourceKey.set(buildGenerationResourceKey(humanAsset), humanAsset.humanAssetType);
  }

  const certifiedByIdentity = new Map<string, number | null | undefined>();
  if (certifiedByResourceKey.size === 0) return certifiedByIdentity;

  for (const reference of references) {
    const resourceKey = buildGenerationResourceKey(reference);
    if (!certifiedByResourceKey.has(resourceKey)) continue;
    certifiedByIdentity.set(
      buildGenerationReferenceIdentityKey(reference),
      certifiedByResourceKey.get(resourceKey)
    );
  }
  return certifiedByIdentity;
}

export function normalizeGenerationReferences(
  references: readonly GenerationReferenceItem[]
): GenerationReferenceItem[] {
  const seen = new Set<string>();
  const normalized: GenerationReferenceItem[] = [];

  for (const reference of references) {
    const identityKey = buildGenerationReferenceIdentityKey(reference);
    if (seen.has(identityKey)) continue;
    seen.add(identityKey);
    normalized.push({
      ...reference,
      ordinal: normalized.length,
      snapshot: { ...reference.snapshot },
    });
  }

  return normalized;
}
